import {
  Box,
  Flex,
  Grid,
  Input,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  useBreakpointValue,
  IconButton,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Button,
  Textarea,
  FormControl,
  FormLabel,
  Spinner,
  GridItem,
  Text,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { CloseIcon, SearchIcon } from "@chakra-ui/icons";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "AppProvider/AppContext";
import FileViewer from "components/common/FileViewerPdfAndImage/FileViewer";
import ForeignForm from "./element/forerignForm"; // ← make sure path is correct
import { appRequest } from "routes";
import { PDFDocument } from "pdf-lib";
import { foreignRequiredFields } from "./element/constant";
import SelectMasterDocumentModal from "./SelectMasterDocumentModal";
import { AttachmentIcon } from "@chakra-ui/icons";

const DOC_SLOTS = [
  { key: "invoice", label: "Invoice Copy", required: true },
  { key: "agreement", label: "Agreement Document", required: false },
  { key: "tax", label: "Tax Documents", required: false },
  { key: "other", label: "Supporting Documents", required: false },
];

/* ================= HELPERS ================= */
const base64ToBlob = (base64: string, contentType: string = "") => {
  const byteCharacters = atob(base64);
  const byteArrays = [];
  const sliceSize = 512 * 1024;

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

/* ================= FIELD MAPPER ================= */
const makeField = (original: any, edited: any) => {
  // Deep comparison for arrays/objects
  const isDifferent = JSON.stringify(original) !== JSON.stringify(edited);
  return {
    originalValue: original ?? "",
    editFieldValue: edited !== undefined && isDifferent ? edited : null,
  };
};

export default function ForeignProcess({
  file,
  isOpen,
  onClose,
  item,
  documentId,
  newData = false,
  handleGet,
  name,
  docId,
  activePage
}: any): JSX.Element {

  const { toastNotify, accountDetails, workflowProcess, workFlowData } =
    useContext(AppContext);
  const viewerWidth = useBreakpointValue({ lg: true });
  const width = file ? "97vw" : "95vw";
  const { isOpen: isExtraDetailsOpen, onOpen: onExtraDetailsOpen, onClose: onExtraDetailsClose } = useDisclosure();
  const extractionDetails = item?.extraDetails?.agreementInvoiceExtractionDetail;

  /* ================= FILE STATE ================= */
  const [fileSlots, setFileSlots] = useState<Record<string, File | null>>({
    invoice: null,
    agreement: null,
    tax: null,
    other: null,
  });

  const [sample, setSample] = useState({
    url: "",
    file: null,
    type: null,
    buffer: null,
  });
  const [fetchFileLoader, setFetchFileLoader] = useState(false);
  const [previews, setPreviews] = useState<Record<string, string | null>>({
    invoice: null,
    agreement: null,
    tax: null,
    other: null,
  });

  /* ================= MASTER DOCUMENT MODAL STATE ================= */
  const [isMasterModalOpen, setIsMasterModalOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState<string | null>(null);

  const handleOpenMasterModal = (key: string) => {
    setActiveSlot(key);
    setIsMasterModalOpen(true);
  };

  const handleMasterDocumentSelect = async (doc: any) => {
    if (!activeSlot || !doc.file) return;

    try {
      setFetchFileLoader(true);
      // Fetch the file content using previewDocument endpoint
      const response: any = await appRequest("document", "previewDocument", {
        fileId: doc.file._id,
      });

      if (response && response.base64) {
        const contentType = response.contentType || doc.file.contentType || "application/pdf";
        const blob = base64ToBlob(response.base64, contentType);

        // Create a File object from the blob
        const file = new File([blob], doc.file.filename || "document.pdf", {
          type: contentType,
        });

        // Update file slots state
        setFileSlots((prev) => ({ ...prev, [activeSlot]: file }));

        // Update preview
        const fileURL = URL.createObjectURL(blob);
        setPreviews((prev) => ({ ...prev, [activeSlot]: fileURL }));

        if (activeSlot !== "invoice" && !fileSlots.invoice && !file) {
          // Check logic if invoice is still missing (though here we just added a file)
          // The original check was inside handleSlotChange
        }
      } else {
        toastNotify({
          title: "Error",
          message: "Failed to download document content",
          type: "error"
        });
      }
    } catch (error) {
      console.error("Error selecting master document:", error);
      toastNotify({
        title: "Error",
        message: "Failed to process selected document",
        type: "error"
      });
    } finally {
      setFetchFileLoader(false);
    }
  };

  /* ================= FORM DATA ================= */
  const [invoiceDetails, setInvoiceDetails] = useState<any>({});
  const [originalDetails, setOriginalDetails] = useState<any>({});

  /* ================= DECISION ================= */
  const [decisionDrawerOpen, setDecisionDrawerOpen] = useState(false);
  const [decisionType, setDecisionType] = useState<"approved" | "rejected">(
    "approved",
  );
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // Toggle state for file viewer

  // Search State
  const [searchDocId, setSearchDocId] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const handleSearchDocument = async () => {
    if (!searchDocId.trim()) {
      toastNotify({
        title: "Error",
        message: "Please enter a Document ID to search",
        type: "error"
      });
      return;
    }

    try {
      setIsSearching(true);
      const response: any = await appRequest("document", "getInitiatorDocValues", {
        documentId: searchDocId.trim()
      });

      if (response?.status === "success" && response.data) {
        // Map the response data using existing helpers
        // The API returns document.values, which is an array of updates.
        const values = response.data;

        // Use the FIRST value (index 0) as requested by the user
        const firstValue = (values && values.length > 0) ? values[0] : {};

        // Flatten the fields structure manually for the first value
        const valuesObj = firstValue.values || {};
        const fields = valuesObj.fields || {};
        const result: any = {};

        Object.keys(fields).forEach((key) => {
          const { originalValue, editFieldValue } = fields[key] || {};
          result[key] =
            editFieldValue !== null && editFieldValue !== undefined
              ? editFieldValue
              : (originalValue ?? "");
        });

        // Check for lineItems as a sibling to fields
        if (valuesObj.lineItems) {
          result.Line_Items = valuesObj.lineItems;
        }

        const mappedForm = mapWorkflowValuesToForm(result);

        // Exclude specific fields from search population as per request
        mappedForm.invoiceNo = "";
        mappedForm.invoiceDate = "";
        mappedForm.paymentDueDate = "";

        setInvoiceDetails(mappedForm);
        setOriginalDetails(mappedForm);

        toastNotify({
          title: "Success",
          message: "Document data populated successfully",
          type: "success"
        });
      } else {
        toastNotify({
          title: "Error",
          message: response?.message || "Document not found",
          type: "error"
        });
      }
    } catch (error: any) {
      console.error("Search error:", error);
      toastNotify({
        title: "Error",
        message: error?.message || "Failed to search document",
        type: "error"
      });
    } finally {
      setIsSearching(false);
    }
  };


  /* ================= FILE HANDLERS ================= */
  const handleSlotChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFileSlots((prev) => ({ ...prev, [key]: selectedFile }));

    // Use Object URL instead of Data URL (Base64) for MUCH faster rendering of large files
    const fileURL = URL.createObjectURL(selectedFile);
    setPreviews((prev) => ({ ...prev, [key]: fileURL }));

    if (key !== "invoice" && !fileSlots.invoice) {
      toastNotify({
        title: "Invoice required",
        message: "Invoice Copy is mandatory",
        type: "error",
      });
    }
  };

  const removeFile = (key: string) => {
    if (previews[key] && previews[key]?.startsWith("blob:")) {
      URL.revokeObjectURL(previews[key]!);
    }
    setFileSlots((prev) => ({ ...prev, [key]: null }));
    setPreviews((prev) => ({ ...prev, [key]: null }));
  };

  /* ================= BUILD WORKFLOW PAYLOAD ================= */
  const buildFieldsPayload = () => ({
    GST_Partner: makeField(
      originalDetails.gstPartner,
      invoiceDetails.gstPartner,
    ),
    Posting_Date: makeField(
      originalDetails.postingDate,
      invoiceDetails.postingDate,
    ),
    Exchange_Rate: makeField(
      originalDetails.exchangeRate,
      invoiceDetails.exchangeRate,
    ),
    Invoice_Type: makeField(
      originalDetails.invoiceType,
      invoiceDetails.invoiceType,
    ),
    Currency: makeField(originalDetails.currency, invoiceDetails.currency),
    Header_Text: makeField(
      originalDetails.headerText,
      invoiceDetails.headerText,
    ),
    Reference_Key_1: makeField(
      originalDetails.referenceKey1,
      invoiceDetails.referenceKey1,
    ),
    Reference_Key_2: makeField(
      originalDetails.referenceKey2,
      invoiceDetails.referenceKey2,
    ),
    taxIdenficationNumber: makeField(
      originalDetails.taxIdenficationNumber,
      invoiceDetails.taxIdenficationNumber,
    ),
    region: makeField(originalDetails.region, invoiceDetails.region),
    Reference_Key_3: makeField(
      originalDetails.referenceKey3,
      invoiceDetails.referenceKey3,
    ),
    TDS_Tax_Code: makeField(
      originalDetails.tdsTaxCode,
      invoiceDetails.tdsTaxCode,
    ),
    Basic_Amount_For_TDS: makeField(
      originalDetails.basicAmountForTDS,
      invoiceDetails.basicAmountForTDS,
    ),
    Company_Code: makeField(
      originalDetails.companyCode,
      invoiceDetails.companyCode,
    ),
    Vendor_Code: makeField(
      originalDetails.vendorCode,
      invoiceDetails.vendorCode,
    ),
    Vendor_Name: makeField(
      originalDetails.vendorName,
      invoiceDetails.vendorName,
    ),
    Vendor_Address: makeField(
      originalDetails.vendorAddress,
      invoiceDetails.vendorAddress,
    ),
    Invoice_Number: makeField(
      originalDetails.invoiceNo,
      invoiceDetails.invoiceNo,
    ),
    Invoice_Date: makeField(
      originalDetails.invoiceDate,
      invoiceDetails.invoiceDate,
    ),
    Invoice_Amount: makeField(
      originalDetails.grandTotal,
      invoiceDetails.grandTotal,
    ),
    Payment_Due_Date: makeField(
      originalDetails.paymentDueDate,
      invoiceDetails.paymentDueDate,
    ),
    TDS_Applicable: makeField(
      originalDetails.tdsApplicable,
      invoiceDetails.tdsApplicable,
    ),
    Line_Items: makeField(originalDetails.lineItems, invoiceDetails.lineItems),
    TDS_Section_Type: makeField(
      originalDetails.tdsSectionType,
      invoiceDetails.tdsSectionType,
    ),
  });

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  };

  const mergeAllFilesToBase64 = async (files: File[]) => {
    const mergedPdf = await PDFDocument.create();

    for (const file of files) {
      if (file.type !== "application/pdf") continue;
      const bytes = await file.arrayBuffer();
      const pdf = await PDFDocument.load(bytes);
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((p) => mergedPdf.addPage(p));
    }
    const mergedBytes: any = await mergedPdf.save();
    return `${arrayBufferToBase64(mergedBytes)}`;
  };

  /* ================= ITEM VALUES → FORM HELPERS ================= */
  const getLatestWorkflowValues = (valuesArray: any[] = []) => {
    if (!Array.isArray(valuesArray) || valuesArray.length === 0) return {};

    const latest = valuesArray[valuesArray.length - 1];
    const valuesObj = latest?.values || {};
    const fields = valuesObj.fields || {};
    const result: any = {};

    Object.keys(fields).forEach((key) => {
      const { originalValue, editFieldValue } = fields[key] || {};
      result[key] =
        editFieldValue !== null && editFieldValue !== undefined
          ? editFieldValue
          : (originalValue ?? "");
    });

    // Check for lineItems as a sibling to fields
    if (valuesObj.lineItems) {
      result.Line_Items = valuesObj.lineItems;
    }

    return result;
  };

  function getPostingDateSimple(approvalDateInput: any) {
    const approvalDate = new Date(approvalDateInput);
    const currentDate = new Date();

    // ✅ Month 0=Jan, 1=Feb, 2=Mar, 3=Apr
    const aMonth = approvalDate.getUTCMonth();
    const cMonth = currentDate.getUTCMonth();
    const cDate = currentDate.getUTCDate();
    const cYear = currentDate.getUTCFullYear();

    // ✅ 1. Was the document created before April 1? (Jan, Feb, Mar)
    const isApprovedBeforeApril = aMonth < 3;

    // ✅ 2. Is today before or on April 5? (Jan, Feb, Mar OR Apr 1-5)
    const isTodayBefore5April = cMonth < 3 || (cMonth === 3 && cDate < 5);

    let postingDate;

    if (isApprovedBeforeApril && isTodayBefore5April) {
      // Force to 31 March 2026, 23:59:59 IST (which is 18:29:59.000Z)
      postingDate = new Date(Date.UTC(cYear, 2, 31, 18, 29, 59, 0));
    } else {
      // Otherwise return actual current date and time
      postingDate = currentDate;
    }

    // ✅ Return as ISO string: 2026-03-19T17:59:00.598Z
    return postingDate
  }

  const mapWorkflowValuesToForm = (v: any = {}) => ({
    gstPartner: v.GST_Partner || "",
    postingDate: new Date() || "",
    exchangeRate: v.Exchange_Rate || "",
    invoiceType: v.Invoice_Type || "",
    region: v.region || "na",
    currency: v.Currency || "",
    headerText: v.Header_Text || "",
    referenceKey1: v.Reference_Key_1 || "",
    referenceKey2: v.Reference_Key_2 || "",
    taxIdenficationNumber: v.taxIdenficationNumber || "",
    referenceKey3: v.Reference_Key_3 || "",
    tdsTaxCode: v.TDS_Tax_Code || "",
    basicAmountForTDS: v.Basic_Amount_For_TDS || "",
    companyCode: v.Company_Code || "",
    vendorCode: v.Vendor_Code || "",
    vendorName: v.Vendor_Name || "",
    vendorAddress: v.Vendor_Address || "",
    invoiceNo: v.Invoice_Number || "",
    invoiceDate: v.Invoice_Date || "",
    grandTotal: v.Invoice_Amount || "",
    paymentDueDate: v.Payment_Due_Date || "",
    tdsApplicable: v.TDS_Applicable || false,
    lineItems: (v.Line_Items || []).map((item: any) => {
      let sectionType = item.tdsSectionType;
      const tdsAmt = Number(item?.tdsAmount || 0);
      const tdsPerc = Number(item?.tdsPercentage || 0);

      if (tdsPerc === 0 && tdsAmt === 0) {
        return {
          ...item,
          tdsSectionType: { label: "NA", value: "NA" },
        };
      }

      // If TDS is present, but it was saved as NA by Level-4, clear it so Level-5 is forced to select it
      const sectionVal = typeof sectionType === "object" && sectionType !== null ? sectionType.value : sectionType;
      if ((tdsAmt > 0 || tdsPerc > 0) && sectionVal === "NA") {
        sectionType = null;
      }

      return {
        ...item,
        tdsSectionType: sectionType
      };
    })
  });

  /* ================= PREFILL FORM FROM ITEM VALUES ================= */
  useEffect(() => {
    if (item?.values?.length) {
      const latestValues = getLatestWorkflowValues(item.values);
      const mappedForm = mapWorkflowValuesToForm(latestValues);

      setInvoiceDetails(mappedForm);
      setOriginalDetails(mappedForm);
    }
  }, [item]);



  const handleExtractData = async (documentId:any) => {
    try {
      if (!fileSlots.invoice || !fileSlots.agreement) {
        // toastNotify({ title: "Validation Error", message: "Both invoice and agreement are required for extraction.", type: "error" });
        return;
      }

      const invoiceBuffer = await fileSlots.invoice.arrayBuffer();
      const agreementBuffer = await fileSlots.agreement.arrayBuffer();

      const invoiceBase64 = arrayBufferToBase64(invoiceBuffer);
      const agreementBase64 = arrayBufferToBase64(agreementBuffer);

      const response: any = await appRequest("document", "extractAgreementInvoice", {
        invoice: invoiceBase64,
        agreement: agreementBase64,
        documentId
      });
    } catch (error: any) {
    } finally {
    }
  };


  const fetchCurrencyEstinate = async () => {
    try {
      console.log('the invoice detail are', invoiceDetails)

      const response: any = await appRequest(
        "foreignEstimate",
        "getForeignEstimateByCurrency",
        {
          currency: invoiceDetails?.currency?.value,
        },
      );

      return response?.data
    }
    catch (err: any) {
      return {}
    }
  }


  /* ================= SUBMIT DECISION ================= */
  const submitDecision = async (decision: "yes" | "no") => {
    try {
      setLoading(true);
      // let user : any = workFlowData?.approvals?.find((it : any) => it.user?.toString() === accountDetails?.user?.toString())
      // let level = user?.level || accountDetails?.level

      let user: any = null;
      if (item && Array.isArray(item?.workflow) && item?.workflow.length > 0) {
        const firstWf = item.workflow[0];
        if (firstWf && Array.isArray(firstWf.values) && firstWf.values.length > 0) {
          const firstVal = firstWf.values[0];
          if (firstVal && Array.isArray(firstVal.approvals)) {
            user = firstVal.approvals.find(
              (it: any) => it?.user?.toString() === accountDetails?.user?.toString()
            );
          }
        }
      }

      let level = user?.level || accountDetails?.level;


      if (level === "level-4" && decisionType === "approved") {
        const missingFieldsSet = new Set<string>();

        (invoiceDetails?.lineItems || []).forEach((item: any) => {
          if (!item.tdsType) missingFieldsSet.add("TDS Type");

          const isTdsNa = item.tdsType === "TDS NA";

          const isTdsPercentageMissing =
            !isTdsNa &&
            (item.tdsPercentage === "" ||
              item.tdsPercentage === null ||
              item.tdsPercentage === undefined);

          if (isTdsPercentageMissing) {
            missingFieldsSet.add("TDS Percentage");
          }

          // 0 is allowed
          const isTdsAmountMissing =
            item.tdsAmount === "" ||
            item.tdsAmount === null ||
            item.tdsAmount === undefined;

          if (isTdsAmountMissing) {
            missingFieldsSet.add("TDS Amount");
          }

          const tdsRemarks = String(item?.tdsRemarks ?? "").trim();

          if (!tdsRemarks || tdsRemarks.length > 40) {
            missingFieldsSet.add("TDS Remarks (Required and max 40 characters)");
          }
        });

        if (missingFieldsSet.size > 0) {
          toastNotify({
            title: "Validation Error",
            message: `Please fill the following mandatory TDS fields for line items: ${Array.from(
              missingFieldsSet
            ).join(", ")}`,
            type: "error",
          });
          setLoading(false);
          return;
        }
      }


      console.log('the level are', level)
      console.log(user)
      // TDS Section Type Validation for Level-5 and Level-6
      if (decisionType === "approved") {
        if (level === "level-5" || level === "level-6" || level === "admin") {

          console.log('the inside log are', level)
          let validationErrorMsg = "";
          const hasInvalidLine = (invoiceDetails?.lineItems || []).some((item: any) => {
            let tdsAmt = parseFloat(item.tdsAmount || 0);
            let tdsPerc = parseFloat(item.tdsPercentage || 0);
            if (isNaN(tdsAmt)) tdsAmt = 0;
            if (isNaN(tdsPerc)) tdsPerc = 0;

            const tdsTypeValue =
              typeof item?.tdsType === "object" && item?.tdsType !== null
                ? item?.tdsType?.value
                : item?.tdsType;
            const sectionValue =
              typeof item?.tdsSectionType === "object" && item?.tdsSectionType !== null
                ? item?.tdsSectionType?.value
                : item?.tdsSectionType;

            const val = String(sectionValue || "").toLowerCase().trim();

            // Rule 1: If TDS Type is NOT "TDS NA", then section becomes mandatory
            if (tdsTypeValue !== "TDS NA" && (!sectionValue || val === "")) {
              validationErrorMsg = "TDS Section Type is mandatory when TDS Type is not 'TDS NA'.";
              return true;
            }

            // Rule 2: If TDS Amount or Percentage is present, section must be Gross-up or Net-up strictly
            if (tdsAmt > 0 || tdsPerc > 0) {
              if (val !== "gross-up" && val !== "net-up") {
                validationErrorMsg = "TDS Section Type must be 'Gross-up' or 'Net-up' when TDS Amount or Percentage is involved.";
                return true;
              }
            } else if (tdsTypeValue === "TDS NA" || (tdsAmt === 0 && tdsPerc === 0)) {
              // Rule 3: If Amount and Percentage are 0, or Type is NA, section must be NA
              if (val !== "na" && val !== "") {
                validationErrorMsg = "TDS Section Type must be 'NA' when TDS Amount and Percentage are 0.";
                return true;
              }
            }

            return false;
          });

          if (hasInvalidLine) {
            toastNotify({
              title: "Validation Error",
              message: validationErrorMsg || "Invalid TDS Section Type for the given TDS values.",
              type: "error",
            });
            setLoading(false);
            return;
          }
        }
      }

      // HSN & Tax Code Validation for Level-3
      if (level === "level-3" && decisionType === "approved") {
        const missingFieldsSet = new Set<string>();

        (invoiceDetails?.lineItems || []).forEach((item: any) => {
          const hsnCode = String(item?.hsnCode ?? "").trim();

          if (!hsnCode || hsnCode.length > 9) {
            missingFieldsSet.add("HSN/SAC Code (Required and max 9 characters)");
          }

          const taxCodeVal = typeof item.taxCode === 'object' && item.taxCode !== null
            ? item.taxCode.value
            : item.taxCode;

          if (!taxCodeVal || taxCodeVal === "") {
            missingFieldsSet.add("Tax Code");
          }
        });

        if (missingFieldsSet.size > 0) {
          toastNotify({
            title: "Validation Error",
            message: `Please fill the following mandatory fields for line items: ${Array.from(missingFieldsSet).join(", ")}`,
            type: "error",
          });
          setLoading(false);
          return;
        }
      }

      // Mandatory Fields Validation for Level-1 and Level-2
      if (decisionType === "approved") {
        if (level === "level-1" || level === "level-2") {
          const mandatoryFields = [
            { key: "profitCenter", label: "Profit Center" },
            { key: "costCenter", label: "Cost Center" },
            // { key: "plantCode", label: "Plant Code" },
            // { key: "businessPlace", label: "Business Place" },
            { key: "businessArea", label: "Business Area" },
            { key: "internalOrder", label: "Internal Order" },
            { key: "glCode", label: "GL Code" },
            { key: "lineNarration", label: "Line Narration" },
            { key: "drCrIndicator", label: "Credit or Debit" }
          ];

          const headerMandatoryFields = [
            { key: "headerText", label: "Header Text" },
            { key: "taxIdenficationNumber", label: "Tax Identification Number" }
          ];

          const missingFieldsSet = new Set<string>();

          headerMandatoryFields.forEach((field) => {
            const value = invoiceDetails?.[field.key];
            const actualValue = (typeof value === 'object' && value !== null && 'value' in value)
              ? value.value
              : value;
            if (actualValue === "" || actualValue === null || actualValue === undefined) {
              missingFieldsSet.add(field.label);
            }
          });

          (invoiceDetails?.lineItems || []).forEach((item: any) => {
            mandatoryFields.forEach((field) => {
              if (!item[field?.key] || item[field.key]?.toString()?.trim() === "") {
                missingFieldsSet?.add(field.label);
              }
            });
          });

          if (missingFieldsSet.size > 0) {
            toastNotify({
              title: "Validation Error",
              message: `Please fill the following mandatory fields for line items: ${Array.from(missingFieldsSet)?.join(", ")}`,
              type: "error",
            });
            setLoading(false);
            return;
          }
        }
      }

      // Global Mandatory Fields Validation (All Levels)
      const globalMandatoryFields = [
        { key: "grandTotal", label: "Grand Total" },
        { key: "invoiceNo", label: "Invoice Number" },
        { key: "vendorName", label: "Vendor Name" },
        { key: "vendorCode", label: "Vendor Code" },
        { key: "vendorAddress", label: "Vendor Address" },
        { key: "companyCode", label: "Company Code" },
        { key: "currency", label: "Currency" },
        { key: "invoiceType", label: "Invoice Type" },
        { key: "invoiceDate", label: "Invoice Date" },
        { key: "paymentDueDate", label: "Payment Due Date" }
      ];

      if (decisionType === "approved") {
        const missingGlobalFields = globalMandatoryFields.filter((field) => {
          const value = invoiceDetails?.[field.key];
          // Handle object values (Select options like { label: "...", value: "..." })
          const actualValue = (typeof value === 'object' && value !== null && 'value' in value)
            ? value.value
            : value;

          // Strict check: Allow 0, but reject "", null, undefined
          return actualValue === "" || actualValue === null || actualValue === undefined;
        });

        if (missingGlobalFields.length > 0) {
          toastNotify({
            title: "Validation Error",
            message: `Please fill the following mandatory fields: ${missingGlobalFields.map((f) => f.label).join(", ")}`,
            type: "error",
          });
          setLoading(false);
          return;
        }
      }

      // Sum of GL Amount must equal Invoice Amount (Grand Total)
      if (decisionType === "approved") {
        const totalGlAmount = (invoiceDetails?.lineItems || []).reduce((sum: number, item: any) => {
          const amt = parseFloat(item.glAmount || 0);
          return sum + (isNaN(amt) ? 0 : amt);
        }, 0);

        const headerTotal = parseFloat(invoiceDetails.grandTotal || 0);

        // String comparison after rounding for exact decimal matching
        if (totalGlAmount.toFixed(2) !== headerTotal.toFixed(2)) {
          toastNotify({
            title: "Validation Error",
            message: `The sum of GL Amounts (${totalGlAmount.toFixed(2)}) must be exactly equal to the Invoice Amount (${headerTotal.toFixed(2)}).`,
            type: "error",
          });
          setLoading(false);
          return;
        }
      }

      // ===================== ADD =====================
      if (!item?.documentId) {
        const files = Object.values(fileSlots).filter((f): f is File => !!f);

        // Validation: At least one file is required for level-1
        if (accountDetails?.level === "level-1" && files.length === 0) {
          toastNotify({
            title: "Validation Error",
            message: "Please upload at least one document.",
            type: "error",
          });
          setLoading(false);
          return;
        }

        let mergedFiles = null;
        let uploadedFileId = null;

        if (files.length) {
          mergedFiles = await mergeAllFilesToBase64(files);

          if (mergedFiles) {
            const CHUNK_SIZE = 1.5 * 1024 * 1024; // 1.5MB safe slices
            const totalChunks = Math.ceil(mergedFiles.length / CHUNK_SIZE);
            const uploadId = `upload_${accountDetails?._id || "anon"}_${Date.now()}_${Math.random().toString(36).substring(7)}`;

            for (let i = 0; i < totalChunks; i++) {
              const start = i * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, mergedFiles.length);
              const chunk = mergedFiles.slice(start, end);

              const chunkResponse: any = await appRequest("document", "uploadChunk", {
                uploadId,
                chunkIndex: i,
                totalChunks,
                chunkData: chunk,
                filename: "attachments.pdf",
                contentType: "application/pdf"
              });

              if (chunkResponse?.status === "success" && chunkResponse?.fileId) {
                uploadedFileId = chunkResponse.fileId;
              } else if (chunkResponse?.status === "error") {
                setLoading(false);
                toastNotify({
                  title: "Upload Failed",
                  message: chunkResponse.message || "Failed to upload file chunk",
                  type: "error"
                });
                return;
              }
            }
          }
        }

        let finalOriginalValues = originalDetails;
        let finalEditedValues = null;

        if (newData) {
          finalOriginalValues = invoiceDetails;
          finalEditedValues = null;
        } else {
          const hasChanges =
            JSON.stringify(originalDetails) !== JSON.stringify(invoiceDetails);
          finalEditedValues = hasChanges ? invoiceDetails : null;
        }

        const fieldsPayload: any = buildFieldsPayload();

        const invoiceNo = String(finalEditedValues?.invoiceNo ?? "").trim();
        if (invoiceNo && invoiceNo.length > 16) {
          console.error("Invoice number cannot be greater than 16 characters");
          toastNotify({
            title: "Validation Error",
            message: "Invoice number cannot be greater than 16 characters",
            type: "error",
          });
          setLoading(false);
          return;
        }

        const headerText = String(finalEditedValues?.headerText ?? "").trim();
        if (headerText && headerText.length > 40) {
          console.error("Header text cannot be greater than 40 characters");
          toastNotify({
            title: "Validation Error",
            message: "Header text cannot be greater than 40 characters",
            type: "error",
          });
          setLoading(false);
          return;
        }

        if (accountDetails?.level === "level-1") {
          const taxIdenficationNumber = String(finalEditedValues?.taxIdenficationNumber ?? "").trim();

          // ✅ Required check
          if (!taxIdenficationNumber) {
            toastNotify({
              title: "Validation Error",
              message: "Tax Identification Number is required",
              type: "error",
            });
            setLoading(false);
            return;
          }

          // ✅ Length check
          if (taxIdenficationNumber.length > 25) {
            toastNotify({
              title: "Validation Error",
              message: "Tax Identification Number cannot be greater than 25 characters",
              type: "error",
            });
            setLoading(false);
            return;
          }
        }



        const { Line_Items, ...fieldsWithoutLines } = fieldsPayload;

        const payload = {
          comment,
          activeWorkflow: workFlowData?._id,
          processName: workflowProcess?.processName,
          processType: workflowProcess?.processType,
          approveDoc: true,
          originalValues: {
            ...finalEditedValues,
            currency:
              finalEditedValues?.currency?.value || finalEditedValues?.currency,
            region:
              finalEditedValues?.region?.value || finalEditedValues?.region,
            companyCode:
              finalEditedValues?.companyCode?.value ||
              finalEditedValues?.companyCode,
            invoiceType:
              finalEditedValues?.invoiceType?.value ||
              finalEditedValues?.invoiceType,
            tdsSectionType:
              finalEditedValues?.tdsSectionType?.value ||
              finalEditedValues?.tdsSectionType,
          },
          editedValues: finalEditedValues,
          finalValues: invoiceDetails,
          values: {
            valuesData: {
              fields: fieldsWithoutLines,
              lineItems: invoiceDetails?.lineItems || [],
            },
          },
          fileId: uploadedFileId || undefined, // INJECT CHUNKED FILE ID
          tags: {
            extraction: {
              type: "Automatic",
            },
          },
        };

        const missingFields = foreignRequiredFields.filter(
          (f) => !String(payload.originalValues?.[f.key] || "").trim(),
        );

        if (missingFields.length) {
          toastNotify({
            title: "Missing Required Fields",
            message: `Please fill: ${missingFields.map((f) => f.label).join(", ")}`,
            type: "error",
          });
          setLoading(false);
          return;
        }

        const response: any = await appRequest(
          "document",
          "createForeignDoc",
          payload,
        );
        if (response?.status === "success") {
          handleExtractData(response?.data?.documentId)
          toastNotify({
            title: "Success",
            message: "Decision submitted successfully",
            type: "success",
          });
          if (onClose) {
            if (handleGet) {
              handleGet()
            }
            onClose();
          }
          setDecisionDrawerOpen(false);
          setComment("");
        } else {
          toastNotify({
            title: "Error",
            message: response?.data || "Failed to Upload the Document",
            type: "error",
          });
        }
      }
      else {


        const fieldsPayload: any = buildFieldsPayload();
        const { Line_Items, ...fieldsWithoutLines } = fieldsPayload;

        const postingDate = getPostingDateSimple(item.approval[0]?.createdAt || new Date())

        const currencyEstimate: any = await fetchCurrencyEstinate()

        const updatedStatus = {
          id: item?._id,
          level: level,
          status: decisionType,
          comment: comment,
          values: {
            fields: { ...fieldsWithoutLines, postingDate: { originalValue: postingDate, editFieldValue: null }, Posting_Date: { originalValue: postingDate, editFieldValue: null }, Exchange_Rate: { originalValue: currencyEstimate?.estimateRate, editFieldValue: null } },
            lineItems: invoiceDetails?.lineItems || [],
          },
          activeWorkflow: workFlowData?._id,
        };

        const response: any = await appRequest(
          "document",
          accountDetails?.level === "admin" ? "updateByAdmin" : "update",
          updatedStatus,
        );

        if (response?.status !== "success") {
          throw new Error(response?.message || "Failed to update document");
        }

        if (onClose) {
          onClose();
          if (handleGet) {
            handleGet()
          }
        }

        toastNotify({
          title: "Success",
          message: response?.message || "Updated successfully",
          type: "success",
        });

        setDecisionDrawerOpen(false);
        setComment("");
      }
    } catch (err: any) {
      toastNotify({
        title: "Error",
        message: err?.message || "Failed to submit decision",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getFile = async () => {
    try {
      if (file?._id && item) {
        setFetchFileLoader(true);
        const response: any = await appRequest("document", "previewDocument", {
          fileId: file?._id,
        });

        if (response && response.base64) {
          const contentType = response.contentType || file.metadata?.contentType || "application/pdf";
          const blob = base64ToBlob(response.base64, contentType);
          const fileURL = URL.createObjectURL(blob);
          setSample({
            url: fileURL,
            file: file,
            type: file.metadata?.contentType || "application/pdf",
            buffer: null,
          });
        }
      }
    } catch (error: any) {
      toastNotify({
        title: "Error",
        message: error.message,
        type: "error",
      });
    } finally {
      setFetchFileLoader(false);
    }
  };

  const getFilesData = async (fileId: any) => {
    try {
      if (fileId) {
        setFetchFileLoader(true);
        const response: any = await appRequest("document", "previewDocument", {
          fileId: fileId,
        });

        if (response && response.base64) {
          const contentType = response.contentType || file.metadata?.contentType || "application/pdf";
          const blob = base64ToBlob(response.base64, contentType);
          const fileURL = URL.createObjectURL(blob);
          setSample({
            url: fileURL,
            file: file,
            type: file.metadata?.contentType || "application/pdf",
            buffer: null,
          });
        }
      }
    } catch (error: any) {
      toastNotify({
        title: "Error",
        message: error.message,
        type: "error",
      });
    } finally {
      setFetchFileLoader(false);
    }
  };

  useEffect(() => {
    if (item && file?._id) {
      getFile();
    }
  }, [item, file?._id]);

  return (
    <>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent maxW={viewerWidth ? width : "100vw"}>
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <Flex align="center" gap={6}>
              <Flex align="center" gap={3}>
                <Text fontSize="lg" fontWeight="semibold">
                  Foreign Documents
                </Text>

                {item?.approval && item?.approval?.length > 0 && item?.approval?.[0]?.name && (
                  <Text fontSize="md" color="gray.600">
                    — {item.approval[0].name}
                  </Text>
                )}
              </Flex>
              {!item && (
                <Flex gap={2} align="center">
                  <InputGroup size="sm" width="300px">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search Document ID..."
                      value={searchDocId}
                      onChange={(e) => setSearchDocId(e.target.value)}
                      borderRadius="md"
                      bg="gray.50"
                      _focus={{ bg: "white", borderColor: "blue.500" }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearchDocument();
                      }}
                    />
                  </InputGroup>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleSearchDocument}
                    isLoading={isSearching}
                  >
                    Search
                  </Button>
                </Flex>
              )}
            </Flex>
          </DrawerHeader>
          <DrawerCloseButton />
          <DrawerBody height="95vh" p={{ base: 2, lg: 4 }}>
            <Grid
              templateColumns={{
                base: "1fr",
                lg: isExpanded ? "1fr" : "35% 65%",
              }} // ← Adjusted for expansion
              gap={isExpanded ? 0 : 4} // Remove gap when expanded
              width="100%"
              height="100%"
              minHeight="0"
            >
              {/* LEFT SIDE - File/Document Viewer or Upload Tabs */}
              <GridItem
                minW="0"
                overflow="hidden"
                display={isExpanded ? "none" : "flex"}
                flexDirection="column"
                position="relative"
              >
                {file?._id && item ? (
                  fetchFileLoader ? (
                    <Box
                      flex={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Spinner
                        size="xl"
                        thickness="4px"
                        speed="0.65s"
                        color="teal.400"
                      />
                    </Box>
                  ) : (
                    <Box flex={1} position="relative" overflow="hidden">
                      <FileViewer activePage={activePage} sample={sample} docId={docId} recallApi={(fileId: any) => { handleGet(); getFilesData(fileId) }} />
                    </Box>
                  )
                ) : (
                  // Upload tabs when no file._id
                  <Tabs
                    isFitted
                    variant="unstyled"
                    flex={1}
                    display="flex"
                    flexDirection="column"
                  >
                    <TabList
                      bg="gray.50"
                      p={2}
                      borderRadius="sm"
                      gap={1}
                      flexShrink={0}
                    >
                      {DOC_SLOTS.map((d) => (
                        <Tab
                          key={d.key}
                          fontSize="xs"
                          fontWeight="600"
                          borderRadius="lg"
                          _selected={{ bg: "teal.500", color: "white" }}
                        >
                          {d.label}
                          {d.required && (
                            <Badge ml={2} colorScheme="red">
                              *
                            </Badge>
                          )}
                        </Tab>
                      ))}
                    </TabList>

                    <TabPanels flex={1} overflow="hidden" mt={2}>
                      {DOC_SLOTS.map((d) => (
                        <TabPanel
                          key={d.key}
                          p={0}
                          height="100%"
                          display="flex"
                          flexDirection="column"
                        >
                          <Flex
                            justify="space-between"
                            align="center"
                            px={3}
                            py={2}
                            flexShrink={0}
                          >
                            {fileSlots[d.key] && (
                              <Badge colorScheme="teal">
                                {fileSlots[d.key]?.name}
                              </Badge>
                            )}
                            {fileSlots[d.key] && (
                              <IconButton
                                size="xs"
                                aria-label="remove"
                                icon={<CloseIcon />}
                                onClick={() => removeFile(d.key)}
                              />
                            )}
                          </Flex>

                          {!fileSlots[d.key] && (
                            <Box px={3} pb={2} flexShrink={0}>
                              <Input
                                type="file"
                                accept=".pdf,.png,.jpg,.jpeg"
                                onChange={(e) => handleSlotChange(e, d.key)}
                              />
                              {!item?.documentId && invoiceDetails?.companyCode && invoiceDetails?.vendorCode && (
                                <Box mt={2} textAlign="center" display={d.key === "invoice" ? 'none' : undefined}>
                                  <Text fontSize="xs" color="gray.500" mb={2}>OR</Text>
                                  <Button
                                    size="sm"
                                    leftIcon={<AttachmentIcon />}
                                    onClick={() => handleOpenMasterModal(d.key)}
                                    variant="outline"
                                    colorScheme="teal"
                                    w="100%"
                                  >
                                    Select from Master Data
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          )}

                          {previews[d.key] && (
                            <Box
                              flex={1}
                              border="1px solid gray.200"
                              overflow="hidden"
                            >
                              <FileViewer
                                sample={{
                                  url: previews[d.key]!,
                                  name: fileSlots[d.key]?.name,
                                  type:
                                    fileSlots[d.key]?.type || "application/pdf",
                                  file: fileSlots[d.key],
                                  buffer: null,
                                }}
                              />
                            </Box>
                          )}
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                )}
              </GridItem>

              {/* RIGHT SIDE - Form */}
              <GridItem
                minW="0"
                overflow="hidden"
                display={"block"}
                position="relative"
              >
                {/* Expand/Collapse Toggle Button for Form */}
                <Box position="absolute" top={2} right={2} zIndex={100}>
                  <IconButton
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                    icon={
                      isExpanded ? (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M4 14h6v6" />
                          <path d="M20 10h-6V4" />
                          <path d="M14 10l7-7" />
                          <path d="M3 21l7-7" />
                        </svg>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M15 3h6v6" />
                          <path d="M9 21H3v-6" />
                          <path d="M21 3l-7 7" />
                          <path d="M3 21l7-7" />
                        </svg>
                      )
                    }
                    size="sm"
                    colorScheme="teal"
                    onClick={() => setIsExpanded(!isExpanded)}
                    opacity={0.8}
                    _hover={{ opacity: 1 }}
                  />
                </Box>
                <Box
                  border="1px solid teal"
                  borderRadius={8}
                  p={{ base: 3, lg: 4 }}
                  height="100%"
                  overflowY="auto" // ← form can scroll when many lines
                >
                  {extractionDetails?.results && (extractionDetails.results.alert_short_summary || extractionDetails.results.alert_summary || extractionDetails.results.invoice_date) && (
                    <Box mb={4} p={3} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" display="flex" justifyContent="space-between" alignItems="center">
                      <Box flex={1} mr={4}>
                        {(() => {
                          const results = extractionDetails?.results;
                          if (!results || !results.invoice_date) return null;
                          const parsedDate = (dStr: any) => {
                            if (!dStr || typeof dStr !== 'string') return null;
                            const parts = dStr.split('-');
                            if (parts.length !== 3) return null;
                            const parsed = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                            return isNaN(parsed.getTime()) ? null : parsed;
                          };
                          const invoiceDate = parsedDate(results.invoice_date);
                          const endDateStr = results.agreement_extended_end_date || results.agreement_original_end_date;
                          const endDate = parsedDate(endDateStr);
                          if (!invoiceDate || !endDate) return null;
                          const isValid = invoiceDate <= endDate;
                          return (
                            <Box mb={1}>
                              <Text fontSize="sm" color="gray.700">
                                <b>Invoice Status :</b> <Badge colorScheme={isValid ? 'green' : 'red'} verticalAlign="baseline" textTransform="uppercase">{isValid ? 'Invoice is Valid' : 'Invoice is Invalid'}</Badge>
                              </Text>
                            </Box>
                          );
                        })()}
                        {(extractionDetails.results.alert_short_summary || extractionDetails.results.alert_summary) && (
                          <Text fontSize="sm" color="gray.700">
                            <b>Invoice Verification :</b> {extractionDetails.results.alert_short_summary}
                          </Text>
                        )}
                      </Box>
                      {(extractionDetails.results.alert_short_summary || extractionDetails.results.alert_summary) && (
                        <Button size="sm" colorScheme="blue" variant="solid" onClick={onExtraDetailsOpen} flexShrink={0}>
                          View Full Report
                        </Button>
                      )}
                    </Box>
                  )}
                  <ForeignForm
                    gstUser={accountDetails?.level === "level-3"}
                    showTds={
                      accountDetails?.level === "admin" ||
                      Number(accountDetails?.level?.split("-")?.[1] || 0) > 3
                    }
                    item={item}
                    disabledOtherUserField={
                      !item?.documentId
                        ? false
                        : accountDetails?.level?.split("-")[1] > 2
                          ? true
                          : false
                    }
                    // disabledOtherUserField={true}
                    tdsFifthApproval={
                      accountDetails?.level === "admin" ||
                      Number(accountDetails?.level?.split("-")?.[1] || 0) > 4
                    }
                    value={invoiceDetails}
                    onChange={(data: any) => {
                      if (!Object.keys(originalDetails).length) {
                        setOriginalDetails(data);
                      }
                      setInvoiceDetails(data);
                    }}
                    workflowProcess={workflowProcess}
                  />
                </Box>
              </GridItem>
            </Grid>
          </DrawerBody>
          <DrawerFooter
            display={name === "Pending Approval" ? undefined : "none"}
          >
            <Flex gap={3}>
              <Button
                colorScheme="green"
                onClick={() => {
                  setDecisionType("approved");
                  setDecisionDrawerOpen(true);
                }}
              >
                Accepted
              </Button>
              <Button
                display={
                  documentId ? accountDetails?.level === "admin" ? "none" : undefined : 'none'
                }
                colorScheme="red"
                onClick={() => {
                  setDecisionType("rejected");
                  setDecisionDrawerOpen(true);
                }}
              >
                Rejected
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Decision Comment Drawer */}
      <Drawer
        isOpen={decisionDrawerOpen}
        placement="bottom"
        onClose={() => setDecisionDrawerOpen(false)}
        size="md"
      >
        <DrawerOverlay backdropFilter="blur(3px)" />
        <DrawerContent borderTopRadius="2xl" p={1}>
          <DrawerHeader borderBottomWidth="1px" pb={3}>
            <Flex align="center" justify="space-between">
              <Text fontSize="lg" fontWeight="semibold">
                {decisionType === "approved"
                  ? "Approval Confirmation"
                  : "Rejection Confirmation"}
              </Text>
              <DrawerCloseButton position="static" />
            </Flex>
          </DrawerHeader>

          <DrawerBody pt={4}>
            <FormControl>
              <FormLabel fontWeight="medium">Comment</FormLabel>
              <Textarea
                placeholder="Please enter your comment..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                resize="none"
                borderRadius="lg"
                focusBorderColor={
                  decisionType === "approved" ? "green.400" : "red.400"
                }
              />
            </FormControl>
          </DrawerBody>
          <DrawerFooter borderTopWidth="1px">
            <Flex w="100%" justify="flex-end" gap={3}>
              <Button
                variant="outline"
                onClick={() => setDecisionDrawerOpen(false)}
              >
                Cancel
              </Button>
              <Button
                colorScheme={decisionType === "approved" ? "green" : "red"}
                isLoading={loading}
                onClick={() => submitDecision("yes")}
                minW="120px"
              >
                {decisionType === "approved" ? "Approve" : "Reject"}
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Modal isOpen={isExtraDetailsOpen} onClose={onExtraDetailsClose} size="2xl" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
        <ModalContent borderRadius="xl" overflow="hidden" boxShadow="2xl">
          <ModalHeader bg="blue.600" color="white" borderBottomWidth="1px" py={4}>
            AI Extraction Verification Report
          </ModalHeader>
          <ModalCloseButton color="white" mt={2} />
          <ModalBody py={6} bg="white">
            {extractionDetails?.results && (
              <Box>
                {(() => {
                  const results = extractionDetails?.results;
                  if (!results || !results.invoice_date) return null;
                  const parsedDate = (dStr: any) => {
                    if (!dStr || typeof dStr !== 'string') return null;
                    const parts = dStr.split('-');
                    if (parts.length !== 3) return null;
                    const parsed = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                    return isNaN(parsed.getTime()) ? null : parsed;
                  };
                  const invoiceDate = parsedDate(results.invoice_date);
                  const endDateStr = results.agreement_extended_end_date || results.agreement_original_end_date;
                  const endDate = parsedDate(endDateStr);
                  if (!invoiceDate || !endDate) return null;
                  const isValid = invoiceDate <= endDate;

                  return (
                    <Flex align="center" mb={5}>
                      <Text fontWeight="bold" fontSize="lg" mr={3} color="gray.700">Overall Status:</Text>
                      <Badge
                        colorScheme={isValid ? 'green' : 'red'}
                        fontSize="md"
                        px={4}
                        py={1.5}
                        borderRadius="full"
                        textTransform="uppercase"
                      >
                        {isValid ? 'Valid' : 'Invalid'}
                      </Badge>
                    </Flex>
                  );
                })()}

                <Box bg="blue.50" p={5} borderRadius="lg" mb={6} borderLeft="4px solid" borderColor="blue.500" boxShadow="sm">
                  <Text fontWeight="bold" color="blue.800" mb={2} fontSize="md">Quick Summary</Text>
                  <Text color="blue.900" fontSize="sm" lineHeight="tall">{extractionDetails.results.alert_short_summary}</Text>
                </Box>

                <Text fontWeight="bold" fontSize="lg" mb={4} color="gray.800" borderBottom="1px solid" borderColor="gray.200" pb={2}>
                  Detailed Analysis
                </Text>

                <Box>
                  {extractionDetails?.results?.alert_summary && String(extractionDetails?.results?.alert_summary || '').split(' | ').map((item: any, index: number) => {
                    if (!item || typeof item !== 'string') return null;
                    const parts = item.split(':');
                    const title = parts[0] || '';
                    const content = parts.slice(1).join(':')?.trim() || '';

                    if (!content) {
                      return (
                        <Box key={index} p={4} mb={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
                          <Text fontSize="sm" color="gray.700" lineHeight="tall">{item}</Text>
                        </Box>
                      );
                    }

                    // Split dynamic content into sentences to render as bullet points
                    const bullets = content ? String(content).split('. ').filter((b: any) => b && typeof b === 'string' && b.trim().length > 0) : [];

                    return (
                      <Box key={index} p={4} mb={3} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" _hover={{ bg: "gray.100", borderColor: "gray.300", shadow: "sm" }} transition="all 0.2s">
                        <Text fontWeight="bold" color="blue.700" fontSize="sm" mb={2}>{title?.trim() || ''}</Text>
                        {bullets?.length > 0 ? (
                          <UnorderedList spacing={2} pl={4}>
                            {bullets.map((bullet: string, i: number) => {
                              const cleanBullet = bullet?.trim() || '';
                              return (
                                <ListItem key={i} fontSize="sm" color="gray.700" lineHeight="tall">
                                  {cleanBullet}{cleanBullet?.endsWith('.') ? '' : '.'}
                                </ListItem>
                              );
                            })}
                          </UnorderedList>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderTopWidth="1px" py={4}>
            <Button colorScheme="blue" variant="solid" onClick={onExtraDetailsClose} px={8} borderRadius="full" shadow="md">
              Close Report
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {isMasterModalOpen && (
        <SelectMasterDocumentModal
          isOpen={isMasterModalOpen}
          onClose={() => setIsMasterModalOpen(false)}
          onSelect={handleMasterDocumentSelect}
          // Pass companyCode/vendorCode if available in invoiceDetails to filter
          companyCode={invoiceDetails?.companyCode?.value || invoiceDetails?.companyCode}
          defaultSearch={invoiceDetails?.vendorName || ""}
          vendorCode={invoiceDetails?.vendorCode?.value || invoiceDetails?.vendorCode}
          slotType={activeSlot || ""}
        />
      )}
    </>
  );
}
